import React from "react";
import { useParams } from "react-router-dom";
import Loader from "../../components/Loader";
import { axiosget } from "../../helpers/httprequest";
import { localRequest } from "../../helpers/uri";
import "./index.css";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import ReceiptHeader from "../../components/ReceiptHeader";
import { useQuery } from "@tanstack/react-query";

const AdmissionLetter = () => {
  const { id } = useParams();

  // Fetch admission letter data
  const { data, isLoading, error } = useQuery({
    queryKey: ["admissionLetter", id],
    queryFn: async () => {
      const req = await axiosget(`ms_applications/letter/${id}`);
      return req?.data;
    },
    enabled: !!id,
  });

  // Fetch officials - MUST be called before any early returns
  const { data: officials } = useQuery({
    queryKey: ["institutionalOfficials"],
    queryFn: async () => {
      const req = await axiosget("ms_officials");
      return req?.data;
    },
    staleTime: 1000 * 60 * 30,
  });

  // Get admission type text
  const getAdmissionType = (status) => {
    switch (status) {
      case 1: return "Provisional";
      case 2: return "Conditional";
      default: return "";
    }
  };

  // Get course duration based on entry type
  const getCourseDuration = (type) => {
    return type === "UTME" ? 4 : 3;
  };

  // Get applicant full name
  const getFullName = () => {
    const bio = data?.bio;
    if (!bio) return "";
    return `${bio.firstname || ""} ${bio.middlename || ""} ${bio.lastname || ""}`.trim().toUpperCase();
  };

  // Get official by role
  const getOfficial = (role) => {
    return officials?.find(v => v.role === role) || {};
  };

  // Early returns AFTER all hooks
  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <Alert variant="danger"><AlertDescription>{`Error loading admission letter: ${error.message}`}</AlertDescription></Alert>;
  }

  if (data?.message) {
    return <Alert variant="danger"><AlertDescription>{data.message}</AlertDescription></Alert>;
  }

  if (!data?.find) {
    return <Alert variant="danger"><AlertDescription>Application data not found</AlertDescription></Alert>;
  }

  const application = data.find;
  const bio = data.bio;
  const admissionType = getAdmissionType(application.status);
  const duration = getCourseDuration(application.type);

  // Get Admission Officer and Registrar from database
  const admissionOfficer = getOfficial("Admission Officer");
  const registrar = getOfficial("Registrar");

  // Use Admission Officer signature if available, else fallback to Registrar
  const signatory = admissionOfficer.name ? admissionOfficer : registrar;
  const signatureUrl = signatory.signature
    ? (signatory.signature.startsWith('http')
      ? signatory.signature
      : `${localRequest}${signatory.signature.replace(/^\\/, '').replace(/^\//, '')}`)
    : "/images/reg.png";
  console.log(signatureUrl)
  return (
    <div className="container">
      <ReceiptHeader
        office={`OFFER OF ${admissionType.toUpperCase()} ADMISSION`}
      />

      {/* Recipient Info */}
      <div className="letterto">
        <div>
          <b>{getFullName()}</b>
          <br />
          <span>{bio?.stateoforigin} State, {bio?.lgaoforigin} LGA</span>
          <br />
          {application?.applicant?.jamb && (
            <span>JAMB REG. NO: <b>{application.applicant.jamb}</b></span>
          )}
        </div>
        <div style={{ textAlign: "right" }}>
          <strong>Date:</strong> {new Date().toLocaleDateString('en-GB', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
        </div>
      </div>

      {/* Letter Body */}
      <div className="body">
        <p>
          After considering your application for admission into the college, I am pleased to write on behalf of the Academic Board to offer you{" "}
          <b>{admissionType} Admission</b> to study <b>{application?.programme?.name || "the selected programme"}</b>
          {application?.programme?.department?.name && (
            <> in the <b>{application.programme.department.name}</b></>
          )}
          {application?.programme?.department?.college?.name && (
            <>, {application.programme.department.college.name}</>
          )} in the{" "}
          <b>{application?.session?.name || "current academic session"}</b>, beginning immediately on the following conditions:
        </p>

        <ul id="letter">
          <li>
            <b>Duration of Course: {duration} year(s)</b>
          </li>
          <li>The particulars provided in your application form are correct in every respect.</li>
          <li>
            You will present at the time of Registration the original copies of all credentials listed in your application for scrutiny. SSCE result slip or its equivalent will be accepted in lieu of certificate.
          </li>
          <li>
            If at any time, even after your registration, it is discovered that you do not satisfy the minimum requirements prescribed for admission, or that any of the qualifications you claimed is false or incorrectly quoted, you will be withdrawn from the College.
          </li>
          <li>You will abide by all the rules and regulations of the College.</li>
          <li>
            You will accept any disciplinary action taken against you by the College management if you contravene any rules and regulations.
          </li>
          <li>
            The name by which you are admitted is the one that will appear on any certificate the College may issue upon successful completion of your course.
          </li>
          <li>This offer is not transferable to another session unless approved.</li>
          <li>The Management reserves the right to withdraw this admission at its discretion.</li>
          <li>
            Your Admission number will be assigned after screening and payment of approved fees. Quote this number in all correspondence. An acceptance letter must be submitted within two weeks of this date, failing which this offer lapses.
          </li>
          <li>
            Upon acceptance, you must pay a non-refundable registration fee as per the attached schedule. The College will not negotiate with any Scholarship Board or Ministry on your behalf.
          </li>
          <li>
            Accommodation is available on a first-come-first-served basis upon payment of appropriate fees. The College does not guarantee accommodation.
          </li>
          <li>
            <b>
              <u>
                This {admissionType.toLowerCase()} admission is subject to production of valid JAMB result for the {application?.session?.name || "current"} Academic Session.
              </u>
            </b>
          </li>
        </ul>

        {/* Signature Section */}
        <div className="signature-section">
          <p style={{ marginBottom: 30 }}>Accept my congratulations.</p>
          <img
            src={signatureUrl}
            alt="Official Signature"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div className="signatory-name">
            {signatory.name || "ALH. SAMBO ALIYU"}{signatory.accolades && <>, <small>{signatory.accolades}</small></>}
          </div>
          <div className="signatory-title">
            {admissionOfficer.name ? "ADMISSION OFFICER" : "REGISTRAR"}
          </div>
        </div>
      </div>

      {/* Print Button */}
      <div className="no-print">
        <button className="print-btn" onClick={() => window.print()}>
          <i className="fa fa-print"></i> Print Admission Letter
        </button>
      </div>
    </div>
  );
};

export default AdmissionLetter;
